import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Instagram, Globe } from 'lucide-react';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const renderIcon = (iconName) => {
  if (iconName === 'instagram') return <Instagram size={16} />;
  if (iconName === 'globe' || iconName === 'website') return <Globe size={16} />;
  return null;
};

const isExternalUrl = (url) => url && (url.startsWith('http') || url.startsWith('//'));

const SmartLink = ({ to, children, className, ...rest }) => {
  if (isExternalUrl(to)) {
    return <a href={to} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
  }
  return <Link to={to} className={className} {...rest}>{children}</Link>;
};

/* Renders paragraphs from body text (split by double newline) */
const BodyText = ({ text, className = '' }) => {
  if (!text) return null;
  const paragraphs = text.split('\n\n').filter(Boolean);
  return (
    <div className={className}>
      {paragraphs.map((p, i) => (
        <p key={i} className="mb-4 last:mb-0">{p}</p>
      ))}
    </div>
  );
};

/* ─── DARK THEME (Film page) ─── */
export const FilmContentSections = ({ sections = [] }) => {
  const sorted = [...sections].filter(s => s.is_active !== false).sort((a, b) => a.order - b.order);
  if (sorted.length === 0) return null;

  return (
    <div className="relative" data-testid="film-content-sections">
      {sorted.map((section, idx) => {
        const imageOnLeft = section.image_position === 'left' || (section.image_position !== 'right' && idx % 2 === 1);

        return (
          <section
            key={section.id || idx}
            className="relative py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-white/5 overflow-hidden"
            data-testid={`film-section-${idx}`}
          >
            {/* Subtle ambient glow */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: idx % 2 === 0
                ? 'radial-gradient(circle at 80% 30%, rgba(212,165,116,0.04) 0%, transparent 50%)'
                : 'radial-gradient(circle at 20% 70%, rgba(212,165,116,0.04) 0%, transparent 50%)'
            }} />

            <div className="max-w-6xl mx-auto relative z-10">
              <div className={`flex flex-col ${imageOnLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}>
                {/* Text */}
                <FadeInSection className="flex-1 w-full" delay={0.1}>
                  <p className="text-[10px] tracking-[0.4em] uppercase text-white/70 mb-4">
                    {String(idx + 1).padStart(2, '0')}
                  </p>
                  <h3
                    className="font-presti text-2xl sm:text-3xl md:text-4xl tracking-[0.03em] text-white/90 mb-6 leading-tight"
                  >
                    {section.title}
                  </h3>
                  <BodyText
                    text={section.body}
                    className="text-white/40 text-sm md:text-base leading-relaxed max-w-xl"
                  />
                  {/* Links */}
                  {section.links && section.links.length > 0 && (
                    <div className="flex flex-wrap items-center gap-4 mt-6">
                      {section.links.map((link, li) => (
                        <SmartLink
                          key={li}
                          to={link.url}
                          className="inline-flex items-center gap-2 text-[#D4A574]/70 hover:text-[#D4A574] text-sm transition-colors"
                          data-testid={`film-section-link-${idx}-${li}`}
                        >
                          {renderIcon(link.icon)}
                          <span>{link.label}</span>
                        </SmartLink>
                      ))}
                    </div>
                  )}
                  {/* CTA Button */}
                  {section.cta_label && section.cta_url && (
                    <div className="mt-8">
                      <SmartLink
                        to={section.cta_url}
                        className="group inline-flex items-center gap-3 px-8 py-3.5 border border-[#D4A574]/30 text-[#D4A574] text-xs tracking-[0.2em] uppercase hover:bg-[#D4A574] hover:text-black transition-all duration-500"
                        data-testid={`film-section-cta-${idx}`}
                      >
                        <span>{section.cta_label}</span>
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </SmartLink>
                    </div>
                  )}
                </FadeInSection>

                {/* Image */}
                {section.image_url && (
                  <FadeInSection className="flex-1 w-full max-w-md lg:max-w-none" delay={0.25}>
                    <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden rounded-sm gold-glow">
                      <img
                        src={section.image_url}
                        alt={section.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 border border-[#D4A574]/10 pointer-events-none" />
                    </div>
                  </FadeInSection>
                )}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

/* ─── LIGHT THEME (Photo page) ─── */
export const PhotoContentSections = ({ sections = [] }) => {
  const sorted = [...sections].filter(s => s.is_active !== false).sort((a, b) => a.order - b.order);
  if (sorted.length === 0) return null;

  return (
    <div className="relative" data-testid="photo-content-sections">
      {sorted.map((section, idx) => {
        const imageOnLeft = section.image_position === 'left' || (section.image_position !== 'right' && idx % 2 === 1);
        const isEven = idx % 2 === 0;

        return (
          <section
            key={section.id || idx}
            className={`relative py-20 md:py-28 px-6 md:px-12 lg:px-24 overflow-hidden ${isEven ? 'bg-white' : 'bg-[#fafafa]'}`}
            data-testid={`photo-section-${idx}`}
          >
            <div className="max-w-6xl mx-auto relative z-10">
              <div className={`flex flex-col ${imageOnLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}>
                {/* Text */}
                <FadeInSection className="flex-1 w-full" delay={0.1}>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-black/25 mb-4">
                    {String(idx + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-presti text-2xl sm:text-3xl md:text-4xl text-black/85 mb-6 leading-tight">
                    {section.title}
                  </h3>
                  <div className="w-16 h-px bg-black/10 mb-6" />
                  <BodyText
                    text={section.body}
                    className="text-black/50 text-sm md:text-base leading-relaxed max-w-xl"
                  />
                  {/* Links */}
                  {section.links && section.links.length > 0 && (
                    <div className="flex flex-wrap items-center gap-4 mt-6">
                      {section.links.map((link, li) => (
                        <SmartLink
                          key={li}
                          to={link.url}
                          className="inline-flex items-center gap-2 text-black/50 hover:text-black text-sm transition-colors border-b border-black/15 hover:border-black pb-0.5"
                          data-testid={`photo-section-link-${idx}-${li}`}
                        >
                          {renderIcon(link.icon)}
                          <span>{link.label}</span>
                        </SmartLink>
                      ))}
                    </div>
                  )}
                  {/* CTA Button */}
                  {section.cta_label && section.cta_url && (
                    <div className="mt-8">
                      <SmartLink
                        to={section.cta_url}
                        className="group inline-flex items-center gap-3 px-8 py-3.5 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/85 transition-all"
                        data-testid={`photo-section-cta-${idx}`}
                      >
                        <span>{section.cta_label}</span>
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </SmartLink>
                    </div>
                  )}
                </FadeInSection>

                {/* Image */}
                {section.image_url && (
                  <FadeInSection className="flex-1 w-full max-w-md lg:max-w-none" delay={0.25}>
                    <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden artistic-shadow">
                      <img
                        src={section.image_url}
                        alt={section.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </FadeInSection>
                )}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};
